import React, { useEffect, useMemo, useState } from "react";
import "./RanksApp.css";
import { ApiPaths } from "../../Config/ApiPath";
import useAxiosHelper from "../../Common/AxiosHelper";
import { BasicInfo } from "../../Config/BasicInfo";
import moment from "moment";
import {
  Trophy,
  Award,
  Users,
  UserCheck,
  Network,
  Check,
  Lock,
  Gift,
  Wallet,
  CalendarClock,
  ChevronDown,
  Inbox,
  RefreshCw,
  Target,
} from "lucide-react";

const tones = ["orange", "blue", "violet", "mint", "pink", "gold", "slate"];

const num = (v) => {
  const n = Number(v);
  return v === undefined || v === null || v === "" || !Number.isFinite(n) ? null : n;
};
const fmt = (v) => (num(v) ?? 0).toLocaleString("en-US", { maximumFractionDigits: 2 });
const money = (v) => `$${fmt(v)}`;

// /get-ranks returns one object per rank. Accept both the field names the
// API sends today and the plan's own names, so the page keeps working if the
// backend is aligned later.
const normalize = (r) => ({
  name: r?.rankname ?? r?.rank_name ?? r?.name ?? "Rank",
  level: num(r?.level) ?? 0,
  achieved: Number(r?.status) === 1,
  achievedDate: r?.ranksAchievedDate && r.ranksAchievedDate !== "-" ? r.ranksAchievedDate : null,
  salary: num(r?.salary),
  salaryDays: num(r?.salary_duration ?? r?.salary_days),
  reward: num(r?.salary_amount ?? r?.reward_amount),
  reqDirects: num(r?.required_directs ?? r?.direct_required),
  reqTeam: num(r?.required_team ?? r?.total_team_required),
  reqFive: num(r?.required_five_directs ?? r?.five_directs_required),
  directs: num(r?.directs) ?? 0,
  team: num(r?.team_members) ?? 0,
  five: num(r?.count_five_directs) ?? 0,
});

const Ranks = () => {
  const { AxiosGet } = useAxiosHelper();
  const [raw, setRaw] = useState(null); // null = loading
  const [error, setError] = useState(false);
  const [openLevel, setOpenLevel] = useState(null);

  async function fetchRanks() {
    setError(false);
    setRaw(null);
    try {
      const res = await AxiosGet(ApiPaths.getRanks);
      // AxiosGet returns undefined when the request fails
      if (!Array.isArray(res)) throw new Error("ranks request failed");
      setRaw(res);
    } catch (e) {
      BasicInfo.isDebug && console.log(e);
      setError(true);
      setRaw([]);
    }
  }

  useEffect(() => {
    fetchRanks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ranks = useMemo(
    () => (raw || []).map(normalize).sort((a, b) => a.level - b.level),
    [raw]
  );

  // current = highest achieved rank; next = first one not achieved above it
  const currentIdx = ranks.reduce((acc, r, i) => (r.achieved ? i : acc), -1);
  const current = currentIdx >= 0 ? ranks[currentIdx] : null;
  const next = ranks.find((r, i) => i > currentIdx && !r.achieved) || null;
  const stats = ranks[0] || { directs: 0, team: 0, five: 0 };
  const achievedCount = ranks.filter((r) => r.achieved).length;
  const pct = ranks.length ? (achievedCount / ranks.length) * 100 : 0;

  // first rank to open by default: the next goal
  useEffect(() => {
    if (openLevel === null && next) setOpenLevel(next.level);
  }, [next, openLevel]);

  const loading = raw === null;

  const req = (label, have, need, Icon) => {
    if (need === null) return null;
    const p = need > 0 ? Math.min(100, (have / need) * 100) : 100;
    const done = have >= need;
    return (
      <div className={`rkReq ${done ? "isDone" : ""}`} key={label}>
        <div className="rkReqTop">
          <span>
            <Icon size={14} /> {label}
          </span>
          <b>
            {fmt(Math.min(have, need > 0 ? Math.max(need, have) : have))} / {fmt(need)}
          </b>
        </div>
        <span className="rkBar">
          <span style={{ width: `${p}%` }} />
        </span>
      </div>
    );
  };

  return (
    <section className="rkPage">
      {/* ---------- Hero ---------- */}
      <div className="rkHero">
        <div className="rkHeroTop">
          <span className="rkEyebrow">
            <Trophy size={13} /> Ranks
          </span>
          <button type="button" className="rkRefresh" onClick={fetchRanks} aria-label="Refresh ranks" disabled={loading}>
            <RefreshCw size={15} className={loading ? "spin" : ""} />
          </button>
        </div>

        {loading ? (
          <div className="rkHeroSkel">
            <span className="a" />
            <span className="b" />
          </div>
        ) : (
          <>
            <div className="rkHeroLabel">Your current rank</div>
            <div className="rkHeroRank">
              <span className="rkHeroBadge">
                <Award size={26} />
              </span>
              <div>
                <b>{current ? current.name : "Unranked"}</b>
                <small>
                  {current
                    ? current.achievedDate
                      ? `Achieved ${moment(current.achievedDate).format("DD MMM YYYY")}`
                      : `Level ${current.level}`
                    : next
                    ? `Next: ${next.name}`
                    : "No ranks available"}
                </small>
              </div>
            </div>

            {ranks.length > 0 && (
              <div className="rkLadderBar" aria-label={`${achievedCount} of ${ranks.length} ranks achieved`}>
                <span className="rkLadderTrack">
                  <span style={{ width: `${pct}%` }} />
                </span>
                <small>
                  {achievedCount} of {ranks.length} ranks achieved
                </small>
              </div>
            )}

            <div className="rkStats">
              <div>
                <UserCheck size={16} />
                <b>{fmt(stats.directs)}</b>
                <small>Active directs</small>
              </div>
              <div>
                <Users size={16} />
                <b>{fmt(stats.team)}</b>
                <small>Total team</small>
              </div>
              <div>
                <Network size={16} />
                <b>{fmt(stats.five)}</b>
                <small>5-direct leaders</small>
              </div>
            </div>
          </>
        )}
      </div>

      {/* ---------- Next goal ---------- */}
      {!loading && next && (
        <div className="rkNext">
          <div className="rkNextHead">
            <span className="rkNextIcon">
              <Target size={18} />
            </span>
            <div>
              <small>Next rank</small>
              <b>{next.name}</b>
            </div>
            {next.reward !== null && next.reward > 0 && (
              <span className="rkNextReward">
                <Gift size={13} /> {money(next.reward)}
              </span>
            )}
          </div>
          {[
            req("Active directs", stats.directs, next.reqDirects, UserCheck),
            req("Total team", stats.team, next.reqTeam, Users),
            req("5-direct leaders", stats.five, next.reqFive, Network),
          ]}
          {next.reqDirects === null && next.reqTeam === null && next.reqFive === null && (
            <p className="rkNoReq">Requirements for this rank aren't available yet.</p>
          )}
        </div>
      )}

      {/* ---------- All ranks ---------- */}
      <div className="rkSectionHead">
        <h2>All ranks</h2>
        {ranks.length > 0 && <span>{ranks.length}</span>}
      </div>

      {loading ? (
        <div className="rkList">
          {[0, 1, 2].map((i) => (
            <div key={i} className="rkCard rkCardSkel">
              <span className="a" />
              <span className="b" />
            </div>
          ))}
        </div>
      ) : error ? (
        <div className="rkEmpty">
          <span>
            <Inbox size={26} />
          </span>
          <b>Couldn't load ranks</b>
          <small>Check your connection and try again.</small>
          <button type="button" onClick={fetchRanks}>
            <RefreshCw size={14} /> Retry
          </button>
        </div>
      ) : ranks.length === 0 ? (
        <div className="rkEmpty">
          <span>
            <Trophy size={26} />
          </span>
          <b>No ranks yet</b>
          <small>Ranks will appear here once they're set up.</small>
        </div>
      ) : (
        <div className="rkList">
          {ranks.map((r, i) => {
            const tone = tones[i % tones.length];
            const open = openLevel === r.level;
            const isNext = next && next.level === r.level;
            const hasReq = r.reqDirects !== null || r.reqTeam !== null || r.reqFive !== null;
            return (
              <article
                key={r.level + r.name}
                className={`rkCard tone-${tone} ${r.achieved ? "isAchieved" : ""} ${isNext ? "isNext" : ""}`}
                style={{ animationDelay: `${Math.min(i, 8) * 0.04}s` }}
              >
                <button
                  type="button"
                  className="rkCardHead"
                  onClick={() => setOpenLevel(open ? -1 : r.level)}
                  aria-expanded={open}
                >
                  <span className="rkLevel">
                    <small>LVL</small>
                    {r.level}
                  </span>
                  <span className="rkCardMain">
                    <b>{r.name}</b>
                    <small>
                      {r.reward !== null && r.reward > 0 ? `Reward ${money(r.reward)}` : "Salary rank"}
                      {r.salary !== null && r.salary > 0 ? ` · Salary ${money(r.salary)}` : ""}
                    </small>
                  </span>
                  <span className={`rkState ${r.achieved ? "on" : isNext ? "next" : "off"}`}>
                    {r.achieved ? (
                      <>
                        <Check size={13} /> Achieved
                      </>
                    ) : isNext ? (
                      "Next"
                    ) : (
                      <>
                        <Lock size={12} /> Locked
                      </>
                    )}
                  </span>
                  <ChevronDown size={18} className={`rkChev ${open ? "isOpen" : ""}`} />
                </button>

                {open && (
                  <div className="rkCardBody">
                    <div className="rkBenefits">
                      <div>
                        <span>
                          <Gift size={14} />
                        </span>
                        <small>Reward</small>
                        <b>{r.reward !== null ? money(r.reward) : "—"}</b>
                      </div>
                      <div>
                        <span>
                          <Wallet size={14} />
                        </span>
                        <small>Salary</small>
                        <b>{r.salary !== null ? money(r.salary) : "—"}</b>
                      </div>
                      <div>
                        <span>
                          <CalendarClock size={14} />
                        </span>
                        <small>Duration</small>
                        <b>{r.salaryDays !== null ? `${fmt(r.salaryDays)} days` : "—"}</b>
                      </div>
                    </div>

                    {hasReq ? (
                      <div className="rkReqs">
                        <p>Requirements</p>
                        {req("Active directs", r.directs, r.reqDirects, UserCheck)}
                        {req("Total team", r.team, r.reqTeam, Users)}
                        {req("5-direct leaders", r.five, r.reqFive, Network)}
                      </div>
                    ) : (
                      <p className="rkNoReq">Requirements for this rank aren't available yet.</p>
                    )}

                    {r.achievedDate && (
                      <p className="rkAchievedOn">
                        <Check size={13} /> Achieved on {moment(r.achievedDate).format("DD MMM YYYY, hh:mm A")}
                      </p>
                    )}
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
};

export default Ranks;
